import React, { useState } from 'react';
import { Box, Button, Chip, Link, Skeleton, Typography } from '@mui/material';
import PanelCard from '../../design/PanelCard';
import PnlText from '../../design/PnlText';
import { formatCurrency, formatCompactCurrency, formatCompactNumber } from '../../design/format';
import { StockProfile } from '../../hooks/useUniverse';

const DESCRIPTION_CLAMP_CHARS = 300;

/** Shape of the useStockProfile hook result, shared by both panels and the header chip. */
export interface ProfileState {
  data: StockProfile | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Parsed from split YYYY-MM-DD parts: Date.parse of the bare string is UTC and shifts a day in western timezones. */
export const earningsChip = (dateStr: string | null): React.ReactNode => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  const earnings = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((earnings.getTime() - today.getTime()) / 86400000);
  if (days < 0) return null;
  if (days === 0) return <Chip label="Earnings today" color="warning" size="small" />;
  if (days <= 7) return <Chip label={`Earnings in ${days}d`} color="warning" size="small" />;
  const monthDay = earnings.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return <Chip label={`Earnings ${monthDay}`} size="small" variant="outlined" />;
};

const recommendationColor = (rec: string): 'success' | 'error' | 'default' => {
  if (rec === 'strong_buy' || rec === 'buy') return 'success';
  if (rec === 'sell' || rec === 'strong_sell') return 'error';
  return 'default';
};

const websiteHost = (url: string): string => {
  try { return new URL(url).hostname; } catch { return url; }
};

const unavailableText = (text: string) => (
  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
    {text}
  </Typography>
);

const buildStatRows = (p: StockProfile): { label: string; value: React.ReactNode }[] => {
  const rows: { label: string; value: React.ReactNode }[] = [];
  if (p.market_cap !== null) rows.push({ label: 'Market cap', value: formatCompactCurrency(p.market_cap) });
  if (p.trailing_pe !== null || p.forward_pe !== null) {
    const parts: string[] = [];
    if (p.trailing_pe !== null) parts.push(p.trailing_pe.toFixed(1));
    if (p.forward_pe !== null) parts.push(`${p.forward_pe.toFixed(1)} fwd`);
    rows.push({ label: 'P/E', value: parts.join(' / ') });
  }
  if (p.beta !== null) rows.push({ label: 'Beta', value: p.beta.toFixed(2) });
  if (p.dividend_yield !== null) rows.push({ label: 'Dividend yield', value: `${p.dividend_yield.toFixed(2)}%` });
  if (p.avg_volume !== null) rows.push({ label: 'Avg volume', value: formatCompactNumber(p.avg_volume) });
  if (p.low_52w !== null && p.high_52w !== null) {
    rows.push({ label: '52w range', value: `${formatCurrency(p.low_52w)} – ${formatCurrency(p.high_52w)}` });
  }
  if (p.pct_off_high !== null) {
    rows.push({ label: '% off 52w high', value: <PnlText value={p.pct_off_high} format="percent" variant="body2" /> });
  }
  if (p.short_pct_of_float !== null) {
    rows.push({ label: 'Short % of float', value: `${(p.short_pct_of_float * 100).toFixed(1)}%` });
  }
  // yfinance emits the literal "none" for uncovered tickers — treat it as no recommendation.
  const recommendation = p.recommendation && p.recommendation !== 'none' ? p.recommendation : null;
  if (recommendation !== null || p.target_mean_price !== null) {
    rows.push({
      label: 'Analyst',
      value: (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
          {recommendation && (
            <Chip label={recommendation.replace(/_/g, ' ')} color={recommendationColor(recommendation)} size="small" />
          )}
          {p.target_mean_price !== null && (
            <Typography variant="body2" component="span">
              Target {formatCurrency(p.target_mean_price)}
              {p.analyst_count !== null ? ` (${p.analyst_count} analysts)` : ''}
            </Typography>
          )}
        </Box>
      ),
    });
  }
  return rows;
};

export const AboutPanel: React.FC<{ profile: ProfileState }> = ({ profile }) => {
  const [expanded, setExpanded] = useState(false);
  const description = profile.data?.description ?? null;
  const isLong = (description?.length ?? 0) > DESCRIPTION_CLAMP_CHARS;
  return (
    <PanelCard title="About" error={profile.error} onRetry={profile.refetch}>
      {profile.loading ? (
        <Skeleton variant="rounded" height={120} />
      ) : (!profile.data?.available || !description) ? (
        unavailableText('Company profile unavailable')
      ) : (
        <>
          <Typography
            variant="body2"
            sx={isLong && !expanded ? {
              display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            } : undefined}
          >
            {description}
          </Typography>
          {isLong && (
            <Button size="small" sx={{ mt: 0.5 }} onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'Show less' : 'Show more'}
            </Button>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 1 }}>
            {profile.data.website && /^https?:\/\//.test(profile.data.website) && (
              <Link href={profile.data.website} target="_blank" rel="noopener noreferrer" variant="body2">
                {websiteHost(profile.data.website)}
              </Link>
            )}
            {profile.data.employees !== null && (
              <Typography variant="body2" color="text.secondary">
                {profile.data.employees.toLocaleString('en-US')} employees
              </Typography>
            )}
          </Box>
        </>
      )}
    </PanelCard>
  );
};

export const KeyStatsPanel: React.FC<{ profile: ProfileState }> = ({ profile }) => {
  const statRows = !profile.loading && profile.data?.available ? buildStatRows(profile.data) : [];
  return (
    <PanelCard title="Key stats" error={profile.error} onRetry={profile.refetch}>
      {profile.loading ? (
        <Skeleton variant="rounded" height={160} />
      ) : statRows.length === 0 ? (
        unavailableText('Key statistics unavailable')
      ) : (
        statRows.map((row) => (
          <Box
            key={row.label}
            sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5,
              '& + &': { borderTop: '1px solid rgba(15, 23, 42, 0.07)' },
            }}
          >
            <Typography variant="caption" color="text.secondary">{row.label}</Typography>
            <Typography
              variant="body2" component="div"
              sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}
            >
              {row.value}
            </Typography>
          </Box>
        ))
      )}
    </PanelCard>
  );
};
